import React, { useState, useRef } from 'react';
import { FormProvider } from 'react-hook-form';
import { flatMap, some } from 'lodash';
import { withStyles } from '@mui/styles';
import {
  Box,
  Stack,
  Divider,
  Grid,
  Tabs as MuiTabs,
  Tab as MuiTab,
  useMediaQuery,
  useTheme,
  List,
  ListItem,
  IconButton,
  Paper,
} from '@mui/material';
import {
  CloseOutlined as CloseIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { ResponseIdFormat } from 'common/utils';
import { Button, Typo } from 'components/primitives';
import { ResourceTabSnippet } from 'components/snippets';
import { AcceptResourcesProps } from './AcceptResources.props';
import useStyles, { TabsStyles, TabItemStyles } from './AcceptResources.styles';
import ResourceTabPanel from '../ResourceTabPanel';
import { IRequestAmbulance, IRequestPersonnel } from 'interfaces/ResourceRequests';
import ResponseStatus from 'common/enums/ResponseStatus';
import { ResourceType, IResourceTab } from 'interfaces/ResourceType';
import { useAppSelector } from 'store/hooks';
import { selectProcessing } from 'store/entities/dashboard/dashboard.selector';
import { ResponseAmbulance } from 'components/fragments/ResponseForm';
import { IResponseAmbulance } from '../../../pages/EMSResponse/hooks/useResponseForm';
import ResponseAmbulanceItems from 'components/fragments/ResponseForm/ResponseAmbulance/ResponseAmbulanceItems';
import AcceptAmbulanceItems from 'components/fragments/AcceptResources/AcceptAmbulanceItems';
import moment from 'moment';
import AcceptPersonnelItems from 'components/fragments/AcceptResources/AcceptPersonnelItems';

const Tabs = withStyles(TabsStyles)(MuiTabs);
const TabItem = withStyles(TabItemStyles)(MuiTab);

const hasResourceAccepted = (arr: IRequestAmbulance[] | IRequestPersonnel[]) => {
  return some(flatMap(arr.map((r) => r.items)), {
    accepted: true,
  });
};

const AcceptResourcesView: React.FC<AcceptResourcesProps> = ({
  requestResponse,
  form,
  tabIndex,
  ...props
}: AcceptResourcesProps) => {
  const theme = useTheme();
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isProcessing = useAppSelector(selectProcessing);
  const { getValues } = form;
  const values = getValues();
  const [openPContent, setOpenPContent] = useState<boolean>(false);
  const [openAContent, setOpenAContent] = useState<boolean>(false);
  const agencyCounty = requestResponse?.agency.county;
  const agencyName = requestResponse?.agency.agencyName;
  const agencyPrimaryName = requestResponse?.agency.poc?.[0]?.fullName || '';
  const agencyPrimaryPhone = requestResponse?.agency.poc?.[0]?.cellPhone || '';
  const agencyState = requestResponse?.agency.state || 'SC';
  const primaryContactNotes =
    requestResponse?.primaryContactNotes ||
    'Agency notes are added here. it could be longer than this, but this will suffice for now.';
  const primaryContactPerson =
    requestResponse?.primaryContactPerson;
  const primaryContactPhone = requestResponse?.primaryContactPhone;
  const incidentName = requestResponse?.requests.incident?.name || '';
  const requestNumber = requestResponse?.requests.requestNumber;
  const responseUpdatedAt = moment(requestResponse?.updatedAt).format('MM/DD/YYYY hh:mm A');
  const countyAddress = `${agencyCounty}, ${agencyState}`;
  const responseId = requestResponse?.id || '';
  const details = values.requests;
  const ambulance = details?.ambulance || [];
  const personnel = details?.personnel || [];
  const [hasEquipmentT, setHasEquipmentT] = useState<boolean>(false);
  const hasAmbulance = values.requests?.ambulance
    ? hasResourceAccepted(values.requests?.ambulance)
    : false;
  const hasPersonnel = values.requests?.personnel
    ? hasResourceAccepted(values.requests?.personnel)
    : false;
  const isAccepted = requestResponse?.status === ResponseStatus.ACCEPTED;
  const parentPRef = useRef<HTMLDivElement>();
  const parentARef = useRef<HTMLDivElement>();

  const resourceTabs: IResourceTab[] = [
    {
      name: 'personnel',
      resource: personnel || [],
    },
    {
      name: 'ambulance',
      resource: ambulance || [],
    },
  ];

  const onAcceptAll = (name: ResourceType, accept: boolean) => {
    let formValue = values;
    if (name === 'ambulance') {
      const defVal: IRequestAmbulance[] = ambulance.map((r) => ({
        ...r,
        items: r?.items && r.items.map((item) => ({ ...item, accepted: accept })),
      }));

      formValue = {
        ...values,
        requests: {
          ...formValue.requests,
          ambulance: defVal,
        },
      };
      form.reset(formValue);
    }
    if (name === 'personnel') {
      const defVal: IRequestPersonnel[] = personnel.map((r) => ({
        ...r,
        items: r?.items && r.items.map((item) => ({ ...item, accepted: accept })),
      }));

      formValue = {
        ...values,
        requests: {
          ...formValue.requests,
          personnel: defVal,
        },
      };
      form.reset(formValue);
    }
  };

  const handleCheck = () => {
    setHasEquipmentT(!hasEquipmentT);
  };

  const handleShowContent = (name: ResourceType) => {
    if (name === 'personnel') {
      setOpenPContent(!openPContent);
    }
    if (name === 'ambulance') {
      setOpenAContent(!openAContent);
    }
  };

  return (
    <Box className={classes.contentStackView}>
      <Paper elevation={0} className={classes.resourcesItem}>
        <Grid
          container
          columnSpacing={1}
          rowSpacing={1}
          sx={{ padding: '0 !important', paddingLeft: '8px !important' }}>
          <Grid item xs={12} sm={6} md={6} lg={6} sx={{ padding: '0 !important' }}>
            <Typo
              sx={{
                color: '#0A1D40',
                fontSize: '16px',
                fontWeight: 700,
                paddingRight: '8px',
              }}>
              {agencyName}
            </Typo>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} className={classes.contactGrid}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typo
                sx={{
                  color: '#515F69',
                  fontSize: '14px',
                  fontWeight: 400,
                  whiteSpace: 'nowrap',
                }}>
                {primaryContactPerson}
              </Typo>
              <div style={{ color: '#2961FD', fontSize: '9px', margin: '2px' }}> {'\u25A0'}</div>
              <Typo
                sx={{ color: '#515F69', fontSize: '14px', fontWeight: 400, whiteSpace: 'nowrap' }}>
                {primaryContactPhone}
              </Typo>
            </Box>
          </Grid>
          <Grid xs={12} sm={12} md={12} lg={12}>
            <div className={classes.detailsText}>{primaryContactNotes}</div>
          </Grid>
        </Grid>
      </Paper>
      <FormProvider {...form}>
        <Stack style={{ width: '100%' }} rowGap={0.75}>
          {/* Loop Row of Ambulance */}
          {(values?.requests?.ambulance ?? [])
            .filter((entry: any) => (entry?.items?.[0]?.number ?? 0) > 0)
            .map((entry: any, i: number) => (
              <React.Fragment key={i}>
                <AcceptAmbulanceItems
                  formName='requests.ambulance'
                  rootName={`requests.ambulance.${i}.items`}
                  field={entry}
                  mainIdx={i}
                  isDisabled={false}
                />
              </React.Fragment>
            ))}
          {(values?.requests?.personnel ?? [])
            .filter((entry: any) => (entry?.items?.[0]?.number ?? 0) > 0)
            .map((entry: any, i: number) => (
              <React.Fragment key={i}>
                <AcceptPersonnelItems
                  formName='requests.personnel'
                  rootName={`requests.personnel.${i}.items`}
                  field={entry}
                  mainIdx={i}
                  isDisabled={false}
                />
              </React.Fragment>
            ))}
        </Stack>
      </FormProvider>
      <Typo
        sx={{
          color: '#5D5D5F',
          fontSize: '14px',
          fontWeight: 400,
          paddingTop: '12px',
          paddingBottom: '12px',
          paddingLeft: '8px',
        }}>
        Accept resources from this agency
      </Typo>
      <Box className={classes.footer}>
        {!isAccepted ? (
          <React.Fragment>
            <Button
              onClick={props.handleDecline(true, values)}
              neutral
              fullWidth
              disabled={isProcessing}>
              Decline
            </Button>
            <div style={{ width: '32px' }} />
            <Button
              progress={isProcessing}
              disabled={false}
              onClick={props.handleAccept(true, values)}
              fullWidth>
              Accept offer
            </Button>
          </React.Fragment>
        ) : (
          <Button onClick={props.handleAccept(false, values)} fullWidth>
            Close
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default AcceptResourcesView;
